import maplibregl from 'maplibre-gl';
import type { Entity } from '../types/entity';
import { color, font } from '../styles/tokens';
import { closeAllPanelsForEntityPopup, registerEntityPopupCloser } from './panelStack';

/**
 * CORREÇÃO DE BUG (não escopo novo por vaidade — ver nota abaixo): antes
 * desta correção, `handleEntityClick` em main.ts era um placeholder que só
 * fazia `console.info(...)` — não existia popup de câmera/entidade real
 * nenhum no projeto. Isso foi confirmado lendo o próprio código (o
 * comentário already dizia "Placeholder até o painel de detalhe ser
 * construído"). O cenário de teste pedido para o Bug 4 ("abrir popup de
 * câmera e depois tocar um chip de camada") só é testável se esse popup
 * existir — por isso ele é construído aqui, com a integração ao
 * panelStack já correta desde a primeira versão (em vez de criar mais um
 * overlay desalinhado que precisaria de outra rodada de correção depois).
 *
 * Usa uma ÚNICA instância de maplibregl.Popup, reaberta com conteúdo novo
 * a cada clique — nunca cria uma segunda instância enquanto a primeira
 * ainda existe, o que por design elimina a possibilidade de popups
 * acumulando na tela (a causa mais comum de "empilhamento" com Popup do
 * MapLibre é justamente instanciar um novo a cada clique sem fechar o
 * anterior).
 */

let activePopup: maplibregl.Popup | null = null;

function closeEntityPopup(): void {
  if (activePopup) {
    activePopup.remove();
    activePopup = null;
  }
}

function buildPopupContent(entity: Entity): HTMLElement {
  const container = document.createElement('div');
  container.style.fontFamily = font.body;
  container.style.minWidth = '160px';

  const title = document.createElement('div');
  title.className = 'heading';
  title.textContent = entity.label;
  title.style.fontSize = '13px';
  title.style.color = color.textPrimary;
  title.style.marginBottom = 'var(--space-2)'; // era 6px, não batia com a escala de 4/8px

  const meta = document.createElement('div');
  meta.className = 'mono';
  meta.style.fontSize = '11px';
  meta.style.color = color.textSecondary;
  meta.textContent = `${entity.layer} · ${new Date(entity.observedAt).toLocaleTimeString('pt-BR')}`;

  container.append(title, meta);

  // Câmeras (Windy/WSDOT/DER-SP) têm previewUrl quando disponível —
  // mostra a imagem se houver, sem quebrar quando não houver (DER-SP
  // nunca tem, ver sources/webcamsDerSp.ts).
  if ('previewUrl' in entity.raw && typeof entity.raw.previewUrl === 'string') {
    const img = document.createElement('img');
    img.src = entity.raw.previewUrl;
    img.alt = entity.label;
    img.style.width = '100%';
    img.style.marginTop = '8px';
    img.style.display = 'block';
    container.appendChild(img);
  }

  return container;
}

/**
 * Abre (ou substitui) o popup de entidade para o clique atual. Fecha
 * qualquer painel HUD (news/recon bottom sheet) que esteja competindo
 * visualmente ANTES de abrir — via closeAllPanelsForEntityPopup, que
 * roda em qualquer breakpoint quando envolve o popup (não só mobile,
 * ver panelStack.ts).
 */
export function openEntityPopup(map: maplibregl.Map, entity: Entity): void {
  closeEntityPopup(); // nunca dois popups simultâneos — fecha o anterior antes de abrir o novo
  closeAllPanelsForEntityPopup();

  const popup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, maxWidth: '240px' })
    .setLngLat([entity.lon, entity.lat])
    .setDOMContent(buildPopupContent(entity))
    .addTo(map);

  popup.on('close', () => {
    if (activePopup === popup) activePopup = null;
  });

  activePopup = popup;
}

/**
 * Registra o fechamento deste popup no panelStack — chamado uma vez na
 * inicialização (main.ts), permite que abrir um chip de camada ou um
 * bottom sheet feche o popup de entidade, completando a exclusividade
 * nos dois sentidos (popup fecha painel ao abrir; painel fecha popup
 * ao abrir).
 */
export function registerEntityPopupWithPanelStack(): void {
  registerEntityPopupCloser(closeEntityPopup);
}
